import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { CASE_STUDIES } from './caseStudiesData';

const ICONS = {
  dollar: '<path d="M12 1v22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>',
};
const Icon = ({ name }) => (
  <span className="cs-impact-icon"
    dangerouslySetInnerHTML={{
      __html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">${ICONS[name] || ICONS.dollar}</svg>`,
    }}
  />
);

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const data = CASE_STUDIES[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <Navigate to="/case-studies" replace />;

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">Case Study</p>
          <h1 className="hero-title">
            {data.titleLead} <span className="gradient-text">{data.titleAccent}</span>
          </h1>
          <p className="hero-description">{data.heroDesc}</p>
          <div className="cs-logos">
            <div className="cs-logo-avenir"><img src="/avenirlogowhite.png" alt="Avenir AI" /></div>
            <span className="cs-logo-x">&times;</span>
            <div className="cs-logo-tile">
              <span style={{ fontFamily: "'Avenir', 'Avenir Next', sans-serif", fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.03em', color: '#111' }}>
                {data.clientType}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality note */}
      <section className="cs-section" style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
        <div className="cs-callout cs-confidential">
          <p className="cs-eyebrow" style={{ marginBottom: '0.4rem' }}>Confidentiality note</p>
          <p>
            This case study is based on an anonymized Avenir workflow. Due to strict
            confidentiality agreements with clients, the savings figures, utilization counts,
            hours saved, and capacity estimates below are illustrative examples designed to show
            the type of analysis Avenir performs.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Overview</p>
        <h2 className="cs-heading">The situation</h2>
        {data.overview.map((p, i) => <p key={i}>{p}</p>)}
      </section>

      {/* Quantified Impact */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Quantified Impact</p>
        <h2 className="cs-heading">What the analysis is worth</h2>
        <div className="cs-impact-grid">
          {data.impact.cards.map((c, i) => (
            <div key={i} className={`cs-impact-card ${c.icon === 'dollar' ? 'is-modeled' : ''}`}>
              <Icon name={c.icon} />
              <div className="cs-impact-value">{c.value}</div>
              <div className="cs-impact-label">{c.label}</div>
              <p className="cs-impact-note">{c.note}</p>
            </div>
          ))}
        </div>
        <h3 className="cs-subhead">{data.impact.basisTitle}</h3>
        <table className="cs-table">
          <thead><tr><th>Assumption</th><th>Value</th></tr></thead>
          <tbody>
            {data.impact.basis.map((r, i) => (
              <tr key={i}><td>{r.label}</td><td>{r.value}</td></tr>
            ))}
          </tbody>
        </table>
        <p style={{ color: '#747064', fontSize: '0.9rem' }}>
          The modeled figure represents the opportunity identified through analysis. Actual
          savings depend on implementation, member engagement, vendor performance, and plan design.
        </p>
      </section>

      {/* Objective */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Objective</p>
        <h2 className="cs-heading">Context, purpose, and process</h2>
        <p>{data.objective.intro}</p>
        <ol className="cs-numlist">
          {data.objective.questions.map((q, i) => <li key={i}>{q}</li>)}
        </ol>
        <p>{data.objective.purpose}</p>
      </section>

      {/* Approach */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Avenir's Approach</p>
        <h2 className="cs-heading">Connecting claims to the real driver</h2>
        {data.approach.map((p, i) => <p key={i}>{p}</p>)}
      </section>

      {/* Hours Saved */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Hours Saved</p>
        <h2 className="cs-heading">Manual workflow versus Avenir</h2>
        <table className="cs-table">
          <thead><tr><th>Manual workflow step</th><th>Estimated time</th></tr></thead>
          <tbody>
            {data.hours.steps.map((s, i) => <tr key={i}><td>{s.label}</td><td>{s.value}</td></tr>)}
            <tr><td>Total estimated manual effort</td><td>{data.hours.total}</td></tr>
            <tr><td>Avenir completion time</td><td>{data.hours.avenir}</td></tr>
          </tbody>
        </table>
        <div className="cs-banner">
          <p className="cs-eyebrow">Total time saved</p>
          <p>{data.hours.saved} recovered on a single employer analysis.</p>
        </div>
      </section>

      {/* Capacity Recovered */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Capacity Recovered</p>
        <h2 className="cs-heading">From one analysis to a full book of business</h2>
        <div className="outcomes-grid" style={{ margin: '1.5rem 0' }}>
          {data.capacity.single.map((c, i) => (
            <div key={i} className="outcome-card">
              <div className="outcome-value">{c.value}</div>
              <div className="outcome-label">{c.label}</div>
            </div>
          ))}
        </div>
        <p>{data.capacity.scaledIntro}</p>
        <table className="cs-table">
          <thead><tr><th>Scaled across the book</th><th>Estimate</th></tr></thead>
          <tbody>
            {data.capacity.scaled.map((r, i) => <tr key={i}><td>{r.label}</td><td>{r.value}</td></tr>)}
          </tbody>
        </table>
        <p>{data.capacity.redirect}</p>
      </section>

      {/* Findings */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Findings</p>
        <h2 className="cs-heading">What the data revealed</h2>
        {data.findings.map((p, i) => <p key={i}>{p}</p>)}
      </section>

      {/* Recommended Action */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Recommended Action</p>
        <h2 className="cs-heading">A targeted, defensible strategy</h2>
        <p>{data.recommended.intro}</p>
        <ul className="cs-keylist">
          {data.recommended.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
        {data.recommended.outro && <p>{data.recommended.outro}</p>}
      </section>

      {/* Why It Matters */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Why It Matters</p>
        <h2 className="cs-heading">Beyond the visible cost</h2>
        {data.whyItMatters.map((p, i) => <p key={i}>{p}</p>)}
      </section>

      {/* Takeaway */}
      <section className="cs-section cs-body">
        <p className="cs-eyebrow">Takeaway</p>
        <h2 className="cs-heading">From cost signal to action</h2>
        {data.takeaway.text.map((p, i) => <p key={i}>{p}</p>)}
        <div className="cs-banner">
          <p className="cs-eyebrow">The Payoff</p>
          <p>{data.takeaway.banner}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">See what Avenir can do for your team</h2>
            <p className="cta-desc">Bring one client's data to a 30-minute demo and we will run a live analysis.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Book a Demo</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetail;
